import { usePopup } from "@contexts/popupContext";
import styles from '@styles/components/FichadaManualModal.module.css';
import { useEffect, useRef, useState } from "react";

import Image from '@components/General/Image';
import ResultPopup from "@components/General/ResultPopup";
import TimeInput from "@components/General/TimeInput";

import { apiConfig, APIIntegration } from '@integrations/APIIntegration';
import { Application } from '@core/Application';
import { DashboardService } from '@services/DashboardService';

import { ReactComponent as ArrowSVG } from '@assets/SVGs/PresenteeismTable/ArrowSVG.svg';
import { ReactComponent as BuildingSVG } from '@assets/SVGs/PresenteeismTable/BuildingSVG.svg';
import { ReactComponent as CalendarSVG } from '@assets/SVGs/PresenteeismTable/CalendarSVG.svg';
import { ReactComponent as ManualSVG } from '@assets/SVGs/PresenteeismTable/ManualSVG.svg';
import { ReactComponent as SearchSVG } from '@assets/SVGs/PresenteeismTable/SearchSVG.svg';

import { DatePickerPopper } from '@components/General/TablePoppers';
import { Loader } from '@shared/designSystem/Loader';

import { format, parse } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

interface BuildingQPOption {
    id: string;
    name: string;
    buildingID: string;
    functionalUnitID: string;
}


function extractDateOnly(dateObj) {
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
}

function createTimestampWithTimeAndTimezone(date, time, timezone) {
    // Date in the format YYYY-MM-DD
    const dateOnlyString = format(date, 'yyyy-MM-dd');

    // Adding the time part
    const combinedDateTimeString = `${dateOnlyString}T${time}:00`;

    // Convert the combined string to a Date object in the given timezone
    const zonedDate = zonedTimeToUtc(parse(combinedDateTimeString, "yyyy-MM-dd'T'HH:mm:ss", new Date()), timezone);

    // Extract the timestamp in milliseconds
    return zonedDate.getTime();
}

const processName = (name) => {
    const words = name.split(' ');
    const processedWords = words.map(word => {
        const firstLetter = word.charAt(0).toUpperCase();
        const restOfWord = word.slice(1).toLowerCase();
        return firstLetter + restOfWord;
    });
    return processedWords.join(' ');        
}

const Dropdown = ({ isOpen, items, onSelect, renderDropdownItem }) => {
    return (
      isOpen && (
        <div className={styles['dropdown']}>
          <div className={styles['dropdown-listado']}>
            {items.map((item, index) => (
              <div
                key={index}
                className={styles['dropdown-item']}
                onClick={() => {
                  onSelect(item);
                }}
              >
                {renderDropdownItem(item)}
              </div>
            ))}
          </div>
        </div>
      )
    );
};

const renderColaboradorItem = (colaborador) => (
    <>
        <div style={{width: '24px', height: '24px', marginRight: '10px', display: 'inline-block', verticalAlign: 'top'}}>
            <Image src={colaborador.image} alt="Profile Picture" isRound={true} style={{ width: '100%', height: '100%' }}/>
        </div>
        <div key={colaborador.id} style={{ display: 'inline-block', maxWidth: 'calc(100% - 34px)', color: '#101828' }}>
            {processName(colaborador.name)}
        </div>
    </>
);

const renderBuildingItem = (building: BuildingQPOption) => (
    <>
        <div style={{width: '24px', height: '24px', marginRight: '10px', display: 'inline-block', verticalAlign: 'top'}}>
            <BuildingSVG/>
        </div>
        <div key={building.id} style={{ display: 'inline-block', maxWidth: 'calc(100% - 34px)', color: '#101828' }}>
            {processName(building.name)}
        </div>
    </>
);

const FichadaManualModal: React.FC<{orgID: string, timezone: string, onSaved?: () => void}> = ({ orgID, timezone, onSaved }) => {
    const { hidePopup, showPopup } = usePopup()
    const [loading, setLoading] = useState(false)
    const [colaboradores, setColaboradores] = useState<any[]>([])
    const [buildings, setBuildings] = useState<BuildingQPOption[]>([])

    const [fichadaType, setFichadaType] = useState("CHECK-IN")

    const [hour, setHour] = useState("")
    const [date, setDate] = useState<Date | null>(null)

    const [openDayPicker, setOpenDayPicker] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [buildingSearchQuery, setBuildingSearchQuery] = useState('')

    const inputRef = useRef(null)
    const buildingInputRef = useRef(null)

    const [selectedColaborador, setSelectedColaborador] = useState<any>()
    const [dropdownColaboradorOpen, setDropdownColaboradorOpen] = useState(false)

    const [selectedBuilding, setSelectedBuilding] = useState<BuildingQPOption>()
    const [dropdownBuildingOpen, setDropdownBuildingOpen] = useState(false)

    const [error, setError] = useState('')


    useEffect(() => {
        setOpenDayPicker(false)
    }, [date])

    // Search collaborators by text against Algolia instead of prefetching the whole roster.
    useEffect(() => {
        if (!dropdownColaboradorOpen) return;

        let cancelled = false;
        const usersService = Application.shared().coreDependencies.usersService;

        const timeoutId = setTimeout(async () => {
            try {
                const { hits } = await usersService.searchUsers(orgID, searchQuery);

                if (cancelled) return;

                const byPhone = new Map<string, typeof hits[number]>();
                hits
                    .filter((item) => item.id?.startsWith('whatsapp:+'))
                    .filter((item) => !item.objectID || item.objectID === item.id)
                    .forEach((item) => {
                        if (!byPhone.has(item.id)) byPhone.set(item.id, item);
                    });

                const mappedResult = Array.from(byPhone.values())
                    .map((item) => ({
                        id: item.id,
                        name: item.name ?? '',
                        mobilePhone: item.id,
                        image: item.image,
                    }));

                setColaboradores(mappedResult);
            } catch (error) {
                console.error("Error searching colaboradores:", error);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [searchQuery, dropdownColaboradorOpen, orgID]);

    // Search QPs/buildings by text against Algolia instead of prefetching all of them
    // (avoids Algolia's 1000-result pagination cap on large orgs).
    useEffect(() => {
        if (!dropdownBuildingOpen) return;

        let cancelled = false;
        const algoliaService = Application.shared().coreDependencies.algoliaService;

        const timeoutId = setTimeout(async () => {
            try {
                const result = await algoliaService.searchQontactPointsV2(
                    orgID,
                    buildingSearchQuery,
                    0,
                    20
                );

                if (cancelled) return;

                const mappedResult: BuildingQPOption[] = result.hits.map(item => ({
                    id: item.id,
                    name: item.buildingName,
                    buildingID: item.buildingID,
                    functionalUnitID: item.functionalUnitID
                }));

                setBuildings(mappedResult);
            } catch (error) {
                console.error("Error buscando edificios/QPs:", error);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [buildingSearchQuery, dropdownBuildingOpen, orgID]);

    const handleBuildingSelect = (building) => {
        setSelectedBuilding(building);
        setBuildingSearchQuery('');
        setDropdownBuildingOpen(false);
    }
    
    const handleClickDayPicker = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenDayPicker((previousOpen) => !previousOpen);
    };
    
    const validateFields = () => {
        if (!selectedColaborador) {
            setError('Por favor selecciona un colaborador')
            return false;
        }
        if (!selectedBuilding) {
            setError('Por favor selecciona un edificio')
            return false;
        }
        if (!date) {
            setError('Por favor selecciona una fecha')
            return false;
        }
        if (!hour) {
            setError('Por favor selecciona una hora')
            return false;
        }
        setError("")
        return true;
    }

    function normalizeTime(time) {
        const [hour, minute] = time.split(":");
        const normalizedHour = parseInt(hour, 10).toString();
        const normalizedMinute = parseInt(minute, 10).toString();
        return `${normalizedHour}:${normalizedMinute.length === 1 ? `0${normalizedMinute}` : normalizedMinute}`;
    }

    const handleSubmit = async () => {
        if (!validateFields()) {
            return;
        }

        setLoading(true)
        try {
            const timestampTimezoned = createTimestampWithTimeAndTimezone(extractDateOnly(date), normalizeTime(hour), timezone)

            const recordData = {
                qpID: selectedBuilding.id,
                userID: selectedColaborador.mobilePhone,
                timestamp: timestampTimezoned,
                type: fichadaType,
            };
            console.log(recordData)
    
            const dashboardService = new DashboardService(Application.shared().coreDependencies.httpClient);
            const result = await dashboardService.createManualRecord(recordData);
            console.log(result);

            // El ResultPopup reemplaza a este modal, así que el aviso al listado se hace
            // acá y no en un onClose: el componente se desmonta apenas cambia el popup.
            onSaved?.()
            showPopup(() => <ResultPopup result={"Fichada manual confirmada!"} succesful={true}/>)
        } catch (error) {
            console.error(error)
            setError("Hubo un error al intentar fichar al colaborador.")
        }
        setLoading(false)
    }

    const handleColaboradorSelect = (colaborador) => {
        setSelectedColaborador(colaborador);
        setSearchQuery('');
        setDropdownColaboradorOpen(false);
    };

    const handleDropdownClick = () => {
        setSelectedColaborador(null);
        if (dropdownColaboradorOpen) {
            setSearchQuery('');
        }
        setDropdownColaboradorOpen((prev) => !prev);
    }

    useEffect(() => {
        if (dropdownColaboradorOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [dropdownColaboradorOpen]);

    const handleBuildingDropdownClick = () => {
        setSelectedBuilding(null);
        if (dropdownBuildingOpen) {
            setBuildingSearchQuery('');
        }
        setDropdownBuildingOpen((prev) => !prev);
    }

    useEffect(() => {
        if (dropdownBuildingOpen && buildingInputRef.current) {
            buildingInputRef.current.focus();
        }
    }, [dropdownBuildingOpen]);

    useEffect(() => {
        console.log("FichadaManualModal - buildings updated:")
        console.log(buildings.filter((building) => building.id !== selectedBuilding?.id).sort((a, b) => (a.name || '').localeCompare(b.name || '')))
    }, [buildings])

    return (
    <div className={styles['modal-container']}>
        <div className={styles['header-container']}>
            <div className={styles['svg-container']} style={{marginRight: '15px'}}>
                <ManualSVG/>
            </div>
            Fichar Colaborador
        </div>

        <div className={styles['field-container']} style={{marginBottom: '30px'}}>
            <div className={styles['input-descriptor']}>Nombre y Apellido</div>
            <div className={styles['input-container']} onClick={handleDropdownClick}>
                {!selectedColaborador ? (
                    <>
                        {dropdownColaboradorOpen ? (
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Buscar colaborador..."
                                className={styles['search-input']}
                            />
                        ) : (
                            <>
                                Elegir colaborador
                                <div className={styles['svg-container']} style={{ marginLeft: 'auto', width: '20px', height: '20px' }}>
                                    <SearchSVG />
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    renderColaboradorItem(selectedColaborador)
                )}
            </div>

            <Dropdown
                isOpen={dropdownColaboradorOpen}
                items={colaboradores
                    .filter((colaborador) => colaborador.id !== selectedColaborador?.id)
                    .sort((a, b) => a.name.localeCompare(b.name))
                }
                onSelect={handleColaboradorSelect}
                renderDropdownItem={renderColaboradorItem}
            />
        </div>

        <div className={styles['middle-fields-container']} style={{marginBottom: '30px'}}>
            <div style={{width: '60%', height: '100%', marginRight: '40px'}}>
                <div className={styles['field-container']}>
                    <div className={styles['input-descriptor']}>Edificio</div>
                    <div className={styles['input-container']} style={{paddingLeft: '20px'}} onClick={handleBuildingDropdownClick}>
                        {!selectedBuilding ?
                            <>
                                {dropdownBuildingOpen ? (
                                    <>
                                        <div className={styles['svg-container']} style={{width: '20px', height: '20px', marginRight: '15px'}}>
                                            <BuildingSVG/>
                                        </div>
                                        <input
                                            ref={buildingInputRef}
                                            type="text"
                                            value={buildingSearchQuery}
                                            onChange={(e) => setBuildingSearchQuery(e.target.value)}
                                            placeholder="Buscar edificio..."
                                            className={styles['search-input']}
                                        />
                                        <div className={styles['svg-container']} style={{ marginLeft: 'auto', width: '20px', height: '20px' }}>
                                            <SearchSVG />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className={styles['svg-container']} style={{width: '20px', height: '20px', marginRight: '15px'}}>
                                            <BuildingSVG/>
                                        </div>
                                        Seleccionar
                                        <div className={styles['svg-container']} style={{marginLeft: 'auto', width: '20px', height: '20px'}}>
                                            <ArrowSVG/>
                                        </div>
                                    </>
                                )}
                            </>
                            : 
                            renderBuildingItem(selectedBuilding)
                        }
                    </div>

                    <Dropdown
                        isOpen={dropdownBuildingOpen}
                        items={buildings
                            .filter((building) => building.id !== selectedBuilding?.id)
                            .filter((building) => building.name.toLowerCase().includes(buildingSearchQuery.toLowerCase()))
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                        onSelect={handleBuildingSelect}
                        renderDropdownItem={renderBuildingItem}
                    />

                </div>
            </div>

            <div style={{width: '40%', height: '100%'}}>
                <div className={styles['field-container']}>
                    <div className={styles['input-descriptor']}>Fecha</div>
                    <div className={styles['input-container']} onClick={handleClickDayPicker}>
                        <div className={styles['svg-container']} style={{width: '20px', height: '20px', marginRight: '15px', color: date ? 'black' : ''}}>
                            <CalendarSVG/>
                        </div>
                        {date ?
                            <p style={{ fontFamily: 'Inter', color: '#101828', fontWeight: '400' }}>{date.getDate()}/{date.getMonth() + 1}/{date.getFullYear()}</p>
                            :
                            "Seleccionar"    
                        }
                    </div>
                    {openDayPicker && <DatePickerPopper 
                        updateDate={setDate} 
                        clickAwayHandler={() => setOpenDayPicker(false)}
                        placement="bottom-end"
                        anchorElement={anchorEl} 
                        isOpen={openDayPicker} 
                        currentPickedDate={date}
                        />
                    }
                </div>
            </div>
        </div>

        <div className={styles['lower-fields-container']}>
            <div style={{width: '25%', height: '100%', minWidth: '135px'}}>
                <div className={styles['field-container']}>
                    <div className={styles['input-descriptor']}>Horario</div>
                    <TimeInput initValue={hour} setHour={setHour} style={{height: '50px', border: '1px solid #DDDFE3', borderRadius: '9px'}}/>
                </div>
            </div>

            <div style={{width: '75%', height: '100%', paddingTop: '25px', paddingLeft: '25px'}}>
                <div className={styles['checkers-container']}>
                    <div className={styles['checker-container']}>
                        <div className={styles['checker']} onClick={() => setFichadaType("CHECK-IN")}>
                            {fichadaType === "CHECK-IN" && 
                                <div className={styles['checker-inner']}/>
                            }
                        </div>
                        Fichar entrada
                    </div>

                    <div className={styles['checker-container']}>
                        <div className={styles['checker']} onClick={() => setFichadaType("CHECK-OUT")}>
                            {fichadaType === "CHECK-OUT" && 
                                <div className={styles['checker-inner']}/>
                            }
                        </div>
                        Fichar salida
                    </div>

                </div>
            </div>
        </div>

        <div className={styles['bottom-container']} style={{marginTop: 'auto'}}>
            <div className={styles['buttons-container']}>
                <div className={`${styles['button']} ${styles['agregar']}`} onClick={handleSubmit}>
                {!loading ? 'Guardar'
                :
                <div className={styles['no-qontact-points']}>
                    <Loader height="32px" width="32px" />
                </div>
                }
                </div>
                <div className={`${styles['button']} ${styles['cancelar']}`} onClick={() => hidePopup()}>
                    Cancelar
                </div>
            </div>
            {error && <div style={{position: 'absolute', top: '-33px', left: '3px', color: 'red'}}>{error}</div>}
        </div>

    </div>
    )
}

export { Dropdown, FichadaManualModal };
